import { memo, useCallback, useMemo, useState, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from '../../../components/base/Modal';
import SaudiRiyalSymbol from '../../../components/base/icons/SaudiRiyalSymbol';
import DomainIcon from '../../../components/base/icons/domain/DomainIcon';
import ListPickerModal from '../../../components/favorites/ListPickerModal';
import ListingBadges from '../../../components/property/ListingBadges';
import { dismissSaveHint } from '../../../components/favorites/SaveHintTooltip';
import { getPropertyTypeIconId } from '../../../config/propertyTypeCatalog';
import { getVisibleFieldGroups } from '../../../config/propertyTypeConfig';
import { useAuth } from '../../../hooks/useAuth';
import { useFavorites } from '../../../hooks/useFavorites';
import { trackContact } from '../../../services/analytics';
import type { Property } from '../../../mocks/properties';
import { getPropertyDisplayTitle } from '../../../utils/arabicPropertyTitle';
import { formatAmount } from '../../../utils/numberFormat';
import { isPriceOnRequest, priceOnRequestLabel } from '../../../utils/propertyUtils';

/**
 * One /search result (owner, 2026-10-07), in the wasalt.sa pattern: the photo,
 * then price, title, «الحي، المدينة» and the specs, then WhatsApp / اتصال. It
 * replaces the full-width PropertyCard (kept in the tree), whose photo carried
 * about ten overlays and whose expand panel held four tabs and a map. That
 * detail lives on the property page, one click away.
 *
 * - The whole card is one stretched link; the heart and the contact buttons sit
 *   above it. A plain click goes through `onSelect` (it records the result
 *   click-through); a modified click opens a new tab as any link does.
 * - THEME.md: the price is teal (structure); the listing pill is neutral ink on
 *   white; colour is spent only on a real state (pending is cyan, sold and
 *   rented are red) and on the saved heart (red); WhatsApp keeps #25D366 as an
 *   outline, never a fill.
 * - Guests: the heart sends them to sign in; a contact button opens the same
 *   «سجّل الدخول للتواصل» prompt as the property page. Both are tracked first.
 */

type ResultProperty = Property & { distanceFromLandmark?: number };

/** Rent periods the listing form stores; anything else gets no suffix rather than a guess. */
const RENT_PERIODS = new Set(['monthly', 'yearly', 'daily']);

const METRIC = 'flex items-center gap-1.5 tabular-nums';
const METRIC_ICON = 'shrink-0 text-[var(--pv-color-primary-light)]';
const PHOTO_ARROW =
  'absolute top-1/2 z-20 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-[var(--pv-color-surface)] text-[var(--pv-color-text)] shadow-sm transition-opacity duration-200 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/40 [@media(hover:none)]:opacity-100';
const CONTACT_BUTTON =
  'relative z-20 inline-flex h-10 items-center justify-center gap-1.5 rounded-full border bg-[var(--pv-color-surface)] text-[0.875rem] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30';

function ResultCard({
  property,
  onSelect,
  landmarkName,
  showListingType = false,
  eagerImage = false,
}: {
  property: ResultProperty;
  /** A plain click on the card (page.tsx records the click-through, then navigates). */
  onSelect: (property: ResultProperty) => void;
  landmarkName?: string;
  /** Show «للبيع» / «للإيجار» on the photo: only useful when the search spans both. */
  showListingType?: boolean;
  eagerImage?: boolean;
}) {
  const { t, i18n } = useTranslation(['search', 'home', 'common', 'property']);
  const isRTL = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isFavorite } = useFavorites();
  const [failedImages, setFailedImages] = useState<ReadonlySet<number>>(() => new Set());
  const [imageIndex, setImageIndex] = useState(0);
  const [showListPicker, setShowListPicker] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  const title = getPropertyDisplayTitle(
    {
      title: property.title,
      titleAr: property.titleAr,
      propertyType: property.propertyType,
      listingType: property.listingType,
      city: property.city,
      cityAr: property.cityAr,
      bedrooms: property.bedrooms,
    },
    isRTL,
  );
  const city = isRTL ? property.cityAr || property.city : property.city;
  const district = isRTL ? property.districtAr || property.district : property.district;
  const place = [district, city].filter(Boolean).join(isRTL ? '، ' : ', ');

  const listing = (property.listingType || '').toUpperCase();
  const isRent = listing === 'RENT' || listing === 'SHORT_RENT';
  const onRequest = isPriceOnRequest(property.price);
  const rentPeriod = isRent && property.rentPeriod && RENT_PERIODS.has(property.rentPeriod) ? property.rentPeriod : null;

  // Bedrooms and bathrooms only where the TYPE has them (land never does), by the
  // same rules as the filters; a listing with no type shows what it carries.
  const type = property.propertyType || '';
  const fields = type ? getVisibleFieldGroups(type) : null;
  const beds = property.bedrooms > 0 && (fields ? fields.bedrooms : true) ? property.bedrooms : 0;
  const baths = property.bathrooms > 0 && (fields ? fields.bathrooms : true) ? property.bathrooms : 0;
  const areaSqm = property.sqft > 0 ? Math.round(property.sqft / 10.7639) : 0;

  // Every photo the listing has, the cover first, each once. The arrows step
  // through them in place (owner, 2026-10-07: the photos could no longer be
  // browsed from the results).
  const images = useMemo(() => {
    const list: string[] = [];
    for (const src of [property.imageUrl, ...(property.galleryImages ?? [])]) {
      if (src && !list.includes(src)) list.push(src);
    }
    return list;
  }, [property.imageUrl, property.galleryImages]);
  const image = images[0] || '';
  const current = images[imageIndex] || '';
  const showImage = Boolean(current) && !failedImages.has(imageIndex);
  const hasGallery = images.length > 1;
  const saved = isFavorite(property.id);
  const phoneDigits = (property.agentPhone || '').replace(/[^0-9]/g, '');

  // The arrows sit above the card's stretched link: a press steps the photo and
  // never opens the listing.
  const stepImage = useCallback(
    (e: MouseEvent, delta: 1 | -1) => {
      e.preventDefault();
      e.stopPropagation();
      setImageIndex((index) => (index + delta + images.length) % images.length);
    },
    [images.length],
  );

  const handleFavorite = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!user) {
        navigate('/signin', { state: { from: location, message: 'signin_to_save_favorites' } });
        return;
      }
      setShowListPicker(true);
    },
    [user, navigate, location],
  );

  // Records the contact click; for guests, opens the sign-in prompt and blocks
  // the action. True only when the contact may go ahead.
  const guardContact = useCallback(
    (method: 'call' | 'whatsapp'): boolean => {
      if (property.id) trackContact('property', property.id, method);
      if (!user) {
        setShowSignIn(true);
        return false;
      }
      return true;
    },
    [property.id, user],
  );

  const closeListPicker = useCallback(() => {
    setShowListPicker(false);
    dismissSaveHint();
  }, []);

  const status = property.status;
  const statusChip =
    status === 'pending'
      ? 'bg-[#E2F6FD] text-[#0B5E78]'
      : status === 'sold' || status === 'rented'
        ? 'bg-[#FDE8E7] text-[#C0322A]'
        : '';

  return (
    <article
      className="group relative flex h-full flex-col overflow-hidden rounded-[16px] border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface)] transition-shadow duration-200 hover:shadow-brand-md"
      dir={i18n.dir()}
    >
      {/* The ring is INSET: the card clips its overflow, so an outer ring would be cut away. */}
      <Link
        to={`/property/${property.id}`}
        aria-label={title}
        className="absolute inset-0 z-10 rounded-[16px] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--pv-color-primary)]"
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
          e.preventDefault();
          onSelect(property);
        }}
      />

      <div className="relative aspect-[16/10] w-full overflow-hidden bg-[var(--pv-color-surface-sunken)]">
        {showImage ? (
          <img
            src={current}
            alt=""
            width={400}
            height={300}
            loading={eagerImage && imageIndex === 0 ? 'eager' : 'lazy'}
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            onError={() => setFailedImages((failed) => new Set(failed).add(imageIndex))}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[#7AC3B7]" aria-hidden="true">
            <DomainIcon id={getPropertyTypeIconId(type)} size="3rem" strokeWidth={1.4} />
          </div>
        )}

        {/* Photo arrows: shown on hover or focus where there is a pointer, always
            on touch screens. «التالية» sits at the inline end, so in Arabic it is
            on the left and moves the way the page reads. */}
        {hasGallery && (
          <>
            <button
              type="button"
              onClick={(e) => stepImage(e, -1)}
              aria-label={t('search.card.prevImage')}
              className={`${PHOTO_ARROW} start-2`}
            >
              <i className={`${isRTL ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-[1.25rem] leading-none`} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={(e) => stepImage(e, 1)}
              aria-label={t('search.card.nextImage')}
              className={`${PHOTO_ARROW} end-2`}
            >
              <i className={`${isRTL ? 'ri-arrow-left-s-line' : 'ri-arrow-right-s-line'} text-[1.25rem] leading-none`} aria-hidden="true" />
            </button>
            <span className="sr-only" aria-live="polite">
              {t('search.card.photoOf', { n: formatAmount(imageIndex + 1), total: formatAmount(images.length) })}
            </span>
            {images.length <= 6 ? (
              <span className="pointer-events-none absolute inset-x-0 bottom-2.5 z-20 flex justify-center gap-1.5" aria-hidden="true">
                {images.map((src, index) => (
                  <span
                    key={src}
                    className={`h-1.5 rounded-full shadow-[0_0_2px_rgba(0,0,0,0.35)] transition-all duration-200 ${index === imageIndex ? 'w-3.5 bg-white' : 'w-1.5 bg-white/65'}`}
                  />
                ))}
              </span>
            ) : (
              <span className="pointer-events-none absolute bottom-2.5 start-2.5 z-20 rounded-full bg-black/55 px-2 py-0.5 text-[0.75rem] font-medium tabular-nums text-white" aria-hidden="true">
                {formatAmount(imageIndex + 1)}/{formatAmount(images.length)}
              </span>
            )}
          </>
        )}

        {/* The state or listing type, then the listing marks: «مميز» and the
            strongest mark it has earned (utils/listingBadges.ts). They wrap
            before the heart. */}
        <div className="absolute start-3 top-3 end-14 flex flex-wrap items-start gap-1.5">
          {(statusChip || showListingType) && (
            <span
              className={[
                'rounded-full px-2.5 py-1 text-[0.8125rem] font-semibold leading-none',
                statusChip || 'bg-[var(--pv-color-surface)] text-[var(--pv-color-text)] ring-1 ring-[var(--pv-border-soft)]',
              ].join(' ')}
            >
              {statusChip
                ? t(`search.statuses.${status}`)
                : isRent
                  ? t('home:home.v2.featured.forRent')
                  : t('home:home.v2.featured.forSale')}
            </span>
          )}
          <ListingBadges listing={property} earnedLimit={1} />
        </div>

        <button
          type="button"
          onClick={handleFavorite}
          aria-pressed={saved}
          aria-label={saved ? t('search.card.removeFromFavorites') : t('search.card.addToFavorites')}
          className="absolute end-3 top-3 z-20 grid h-9 w-9 place-items-center rounded-full bg-[var(--pv-color-surface)] text-[var(--pv-color-text)] shadow-sm transition-colors hover:text-[#C0322A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/40"
        >
          <i
            className={`${saved ? 'ri-heart-fill text-[#C0322A]' : 'ri-heart-line'} text-[1.125rem] leading-none`}
            aria-hidden="true"
          />
        </button>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <p className="flex flex-wrap items-baseline gap-x-1.5 text-[1.25rem] font-bold leading-snug text-[var(--pv-color-primary)] tabular-nums">
          {onRequest ? (
            <span className="text-base font-semibold text-[var(--pv-color-text)]">{priceOnRequestLabel(isRTL)}</span>
          ) : (
            <>
              <SaudiRiyalSymbol className="h-[0.9em] w-[0.9em] shrink-0 self-center" title="" aria-hidden="true" />
              <span>{formatAmount(property.price, { maximumFractionDigits: 0 })}</span>
              <span className="sr-only">{t('common:currency.name')}</span>
              {rentPeriod && (
                <span className="text-[0.875rem] font-medium text-[var(--pv-color-text-muted)]">
                  /{t(`home:home.v2.featured.period.${rentPeriod}`)}
                </span>
              )}
            </>
          )}
        </p>

        <h3 className="mt-1 line-clamp-1 text-[0.9375rem] font-semibold text-[var(--pv-color-text)]">{title}</h3>

        {place && (
          <p className="mt-1 flex items-center gap-1 text-[0.875rem] text-[var(--pv-color-text-muted)]">
            <i className="ri-map-pin-line shrink-0 text-base leading-none" aria-hidden="true" />
            <span className="line-clamp-1 min-w-0">{place}</span>
          </p>
        )}

        {property.distanceFromLandmark !== undefined && landmarkName && (
          <p className="mt-1 flex items-center gap-1 text-[0.8125rem] text-[var(--pv-color-text-muted)]">
            <i className="ri-route-line shrink-0 text-base leading-none" aria-hidden="true" />
            <span className="line-clamp-1 min-w-0">
              {t('search.card.distanceFrom', {
                km: formatAmount(property.distanceFromLandmark, { maximumFractionDigits: 1 }),
                place: landmarkName,
              })}
            </span>
          </p>
        )}

        {(beds > 0 || baths > 0 || areaSqm > 0) && (
          <ul role="list" className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[0.875rem] text-[var(--pv-color-text-muted)]">
            {beds > 0 && (
              <li className={METRIC}>
                <DomainIcon id="bedroom" size="1.125rem" className={METRIC_ICON} />
                <span aria-hidden="true">{formatAmount(beds)}</span>
                <span className="sr-only">{t('home:home.v2.featured.beds', { count: beds, n: formatAmount(beds) })}</span>
              </li>
            )}
            {baths > 0 && (
              <li className={METRIC}>
                <DomainIcon id="bathroom" size="1.125rem" className={METRIC_ICON} />
                <span aria-hidden="true">{formatAmount(baths)}</span>
                <span className="sr-only">{t('home:home.v2.featured.baths', { count: baths, n: formatAmount(baths) })}</span>
              </li>
            )}
            {areaSqm > 0 && (
              <li className={METRIC}>
                <DomainIcon id="area" size="1.125rem" className={METRIC_ICON} />
                <span className="sr-only">{t('home:home.v2.featured.area')}</span>
                <span>
                  {formatAmount(areaSqm)} {t('common:units.sqm')}
                </span>
              </li>
            )}
          </ul>
        )}

        {phoneDigits && (
          <div className="mt-auto grid grid-cols-2 gap-2 pt-4">
            <button
              type="button"
              onClick={() => {
                if (guardContact('whatsapp')) window.open(`https://wa.me/${phoneDigits}`, '_blank', 'noopener,noreferrer');
              }}
              className={`${CONTACT_BUTTON} border-[#25D366]/60 text-[var(--pv-color-text)] hover:border-[#25D366]`}
            >
              <i className="ri-whatsapp-line text-[1.125rem] leading-none text-[#1DA851]" aria-hidden="true" />
              {t('property:property.contact.whatsapp')}
            </button>
            <a
              href={`tel:${property.agentPhone}`}
              onClick={(e) => {
                if (!guardContact('call')) e.preventDefault();
              }}
              className={`${CONTACT_BUTTON} border-[var(--pv-border-strong)] text-[var(--pv-color-text)] hover:border-[var(--pv-color-primary)]`}
            >
              <i className="ri-phone-line text-[1.125rem] leading-none text-[var(--pv-color-primary)]" aria-hidden="true" />
              {t('search.card.call')}
            </a>
          </div>
        )}
      </div>

      {/* Mounted only while open: a page holds twenty cards. */}
      {showListPicker && (
        <ListPickerModal
          isOpen
          onClose={closeListPicker}
          propertyId={property.id}
          propertyTitle={title}
          propertyImageUrl={image || null}
          onSaved={dismissSaveHint}
        />
      )}

      {showSignIn && (
        <Modal
          isOpen
          onClose={() => setShowSignIn(false)}
          title={t('property:property.contact.authPrompt.title')}
          size="sm"
        >
          <p className="mb-5 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
            {t('property:property.contact.authPrompt.message')}
          </p>
          <div className="flex flex-col gap-2.5">
            <button
              type="button"
              onClick={() => navigate('/signin', { state: { from: location } })}
              className="h-11 w-full rounded-full bg-[var(--pv-color-primary)] text-[0.9375rem] font-semibold text-white transition-opacity hover:opacity-90 dark:text-[var(--pv-color-surface)]"
            >
              {t('property:property.contact.authPrompt.signIn')}
            </button>
            <button
              type="button"
              onClick={() => setShowSignIn(false)}
              className="h-11 w-full rounded-full border border-[var(--pv-border-strong)] text-[0.9375rem] font-medium text-[var(--pv-color-text)] transition-colors hover:border-[var(--pv-color-primary)]"
            >
              {t('property:property.contact.authPrompt.cancel')}
            </button>
          </div>
        </Modal>
      )}
    </article>
  );
}

export default memo(ResultCard);
